import React from 'react';
import PropTypes from 'prop-types';
import { ResponsivePie } from '@nivo/pie';

const CenteredMetric = (metrics) => (layerProps) => {
  const { centerX, centerY } = layerProps;
  let renderedMetrics;

  if (!metrics.top && !metrics.bottom) {
    renderedMetrics = '';
  } else if (!metrics.bottom) {
    renderedMetrics = (
      <text
        x={centerX}
        y={centerY}
        textAnchor="middle"
        dominantBaseline="central"
        style={{
          fontSize: '1.5rem',
          fontWeight: 600,
        }}
      >
        {metrics.top}
      </text>
    );
  } else {
    renderedMetrics = (
      <>
        <text
          x={centerX}
          y={centerY-25}
          textAnchor="middle"
          dominantBaseline="central"
          style={{
            fontSize: '5rem',
            fontWeight: 600,
          }}
        >
          {metrics.top}
        </text>
        <text
          x={centerX}
          y={centerY+40}
          textAnchor="middle"
          dominantBaseline="central"
          style={{
            fontSize: '1rem',
            fontWeight: 600,
          }}
        >
          Industry
        </text>
        <text
          x={centerX}
          y={centerY+65}
          textAnchor="middle"
          dominantBaseline="central"
          style={{
            fontSize: '1.5rem',
            fontWeight: 600,
          }}
        >
          {metrics.bottom}
        </text>
      </>
    );
  }

  return renderedMetrics;
};

const PieChart = ({ data, metrics = {}, isInteractive, showLegend = true, ...restProps }) => {
  const defaultProps = {
    margin: { top: 40, right: 20, bottom: 120, left: 20 },
    innerRadius: 0.6,
    padAngle: 1,
    cornerRadius: 0,
    activeOuterRadiusOffset: 8,
    borderWidth: 1,
    borderColor: {
      from: 'color',
      modifiers: [['darker', 0.2]],
    },
    enableArcLabels: true,
    arcLabel: value => {
      const total = data.reduce((sum, item) => sum + item.value, 0);
      const percentage = ((value.value / total) * 100).toFixed(0);
      return `${percentage}%`;
    },
    arcLabelsTextColor: '#ffffff',
    arcLabelsSkipAngle: 10,
    theme: {
      labels: {
        text: {
          fontSize: 18,
          fontWeight: 700,
          fill: '#ffffff'
        }
      }
    },
    valueFormat: value => `${Number(value).toLocaleString('en-US', {
      minimumFractionDigits: 0,
    })} %`,
    colors: ({ data }) => data.color,
    legends: showLegend ? [{
      anchor: 'bottom',
      direction: 'column',
      justify: false,
      translateX: 0,
      translateY: 120,
      itemWidth: 100,
      itemHeight: 20,
      itemsSpacing: 8,
      itemTextColor: '#666',
      itemDirection: 'left-to-right',
      itemOpacity: 1,
      symbolSize: 20,
      symbolShape: 'circle'
    }] : [],
    tooltip: ({ datum }) => (
      <div style={{
        padding: '8px',
        background: 'white',
        border: '1px solid #ccc',
        borderRadius: '4px'
      }}>
        {`$${datum.value.toLocaleString('en-US', {
          style: 'decimal',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`}
      </div>
    ),
  };

  return (
    <ResponsivePie
      {...defaultProps}
      {...restProps}
      data={data}
      isInteractive={isInteractive}
      layers={['arcs', 'arcLabels', CenteredMetric(metrics), ...(showLegend ? ['legends'] : [])]}
    />
  );
};

PieChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    color: PropTypes.string.isRequired
  })).isRequired,
  metrics: PropTypes.shape({
    top: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    bottom: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  isInteractive: PropTypes.bool,
  showLegend: PropTypes.bool
};

export default PieChart;
